const BOSTON_ADAPTER_VERSION = 'boston-data-logic-v1'

const BOSTON_FIELD_TYPES = new Set([
  'text', 'number', 'decimal', 'mail', 'date', 'date_sabre',
  'date_dd_mm_yy', 'hour_sabre', 'select', 'textarea', 'check',
  'checkbox', 'button', 'label',
])

function compactObject(value) {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => {
      if (entry === undefined || entry === null) return false
      if (Array.isArray(entry) && entry.length === 0) return false
      return true
    }),
  )
}

function normalizeName(value = '') {
  return String(value || '')
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .replace(/^@/, '')
    .replace(/#[A-Z]$/i, '')
    .replace(/:$/, '')
    .trim()
    .toUpperCase()
}

function slugify(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
}

function uniqueId(base, used) {
  const clean = slugify(base) || 'item'
  let candidate = clean
  let suffix = 2
  while (used.has(candidate)) {
    candidate = `${clean}_${suffix}`
    suffix += 1
  }
  used.add(candidate)
  return candidate
}

function normalizeDatasource(datasource = []) {
  return (Array.isArray(datasource) ? datasource : [])
    .map((option) => {
      if (option && typeof option === 'object') {
        const value = option.value ?? option.id ?? option.code ?? option.text ?? option.label ?? ''
        const text = option.text ?? option.label ?? option.name ?? option.value ?? ''
        return { ...option, value: String(value ?? ''), text: String(text ?? '') }
      }
      return { value: String(option ?? ''), text: String(option ?? '') }
    })
    .filter((option) => String(option.value || option.text || '').trim())
}

function normalizeBostonType(field = {}) {
  const current = String(field.typedata || field.type || '').trim().toLowerCase()
  if (current === 'numeric' || current === 'num' || current === 'integer') return 'number'
  if (current === 'checkbox') return 'check'
  if (current === 'select_menu') return 'select'
  if (current === 'button_menu') return 'button'
  if (current === 'hidden') return 'text'
  return BOSTON_FIELD_TYPES.has(current) ? current : 'text'
}

function fieldKey(field = {}, formId = '', index = 0) {
  const type = normalizeBostonType(field)
  const raw = field.binding || field.variable || field.name || field.id || `${type}_${index + 1}`
  const normalized = slugify(normalizeName(raw)) || `${type}_${index + 1}`
  if (type === 'label' || type === 'button') return `${slugify(formId) || 'form'}_${normalized}_${index + 1}`
  return normalized
}

function mapCheckGroup(field, context) {
  return (field.options || []).map((option, index) => {
    const key = fieldKey(option, context.formId, context.index + index)
    return compactObject({
      tag: key,
      campoForm: key,
      label: option.text || option.label || option.variable || `${field.label || 'Opcion'} ${index + 1}`,
      typedata: 'check',
      visible: field.visible !== false,
      mandatory: false,
      dato: option.value ?? '',
      default: option.value ?? '',
      remark: '',
      remark_mask: '',
      skip: true,
      processed: false,
      group: context.group,
      source_additional: {
        source: 'sst_converter',
        sourceForm: context.sourceFormId,
        sourceField: field.name,
        binding: option.variable || option.binding || option.name,
        route: context.route,
      },
    })
  })
}

function mapField(field, context) {
  if (!field) return []
  if (String(field.typedata || '').toLowerCase() === 'check_group') {
    return mapCheckGroup(field, context)
  }

  const typedata = normalizeBostonType(field)
  const key = fieldKey(field, context.formId, context.index)
  const visible = field.visible !== false
  const visualOnly = typedata === 'label' || typedata === 'button'
  const readonly = Boolean(field.readonly || field.displayOnly)
  const mappedType = readonly && !visualOnly ? 'label' : typedata
  const datasource = mappedType === 'select' ? normalizeDatasource(field.datasource || field.options) : []
  const value = field.value ?? field.default ?? ''

  return [compactObject({
    tag: key,
    campoForm: key,
    label: field.label || field.text || field.binding || field.name || '',
    typedata: mappedType,
    visible,
    readonly,
    displayOnly: Boolean(field.displayOnly),
    mandatory: !visualOnly && !readonly && visible ? Boolean(field.mandatory || field.required) : false,
    maxlength: field.maxlength || field.length,
    dato: mappedType === 'label' ? (field.textTemplate || field.templateValue || field.label || value) : value,
    default: !visualOnly && value !== '' ? value : undefined,
    datasource,
    coincidence: field.coincidence,
    remark: '',
    remark_mask: '',
    skip: true,
    processed: false,
    group: visible ? context.group : '*OCULTO*',
    source_additional: {
      source: 'sst_converter',
      sourceForm: context.sourceFormId,
      sourceLine: field.sourceLine || field.line,
      binding: field.binding || field.variable || field.name,
      classification: field.classification,
      route: context.route,
      layout: compactObject({ row: field.row, column: field.column, raw: field.raw }),
    },
  })]
}

function findByNormalizedName(collection = {}, target = '') {
  const wanted = normalizeName(target)
  if (!wanted) return null
  for (const [key, value] of Object.entries(collection || {})) {
    if ([key, value?.id, value?.legacyId, value?.title].some((candidate) => normalizeName(candidate) === wanted)) {
      return { key, value }
    }
  }
  return null
}

function createProgramWalker(logicJson = {}, diagnostics = []) {
  const blocks = logicJson.blocks || {}
  const forms = logicJson.forms || {}
  const menus = logicJson.menus || {}

  function walkActions(actions = [], state, stack) {
    for (const action of actions || []) {
      if (!action) continue
      state.actionCount += 1
      if (state.actionCount > 10000) {
        diagnostics.push({ level: 'warning', code: 'BOSTON_ADAPTER_ACTION_LIMIT', message: 'Se detuvo una ruta que excedio 10000 acciones.' })
        return true
      }
      if (action.type === 'if') {
        walkActions(action.then || [], state, stack)
        walkActions(action.else || [], state, stack)
        continue
      }
      if (action.type === 'window' || action.type === 'showWindow') {
        const form = findByNormalizedName(forms, action.target || action.formRef || action.id)
        if (form && !state.forms.includes(form.key)) state.forms.push(form.key)
        continue
      }
      if (action.type === 'showMenu') {
        const menu = findByNormalizedName(menus, action.target)
        if (menu && !state.menus.includes(menu.key)) state.menus.push(menu.key)
        return true
      }
      if (action.type === 'call') {
        walkBlock(action.target, state, stack)
        continue
      }
      if (action.type === 'goto') {
        walkBlock(action.target, state, stack)
        return true
      }
      if (['exit', 'stop', 'return'].includes(action.type)) return true
    }
    return false
  }

  function walkBlock(target, state, stack = []) {
    const found = findByNormalizedName(blocks, target)
    if (!found) return
    if (stack.includes(found.key)) {
      diagnostics.push({
        level: 'warning',
        code: 'BOSTON_ADAPTER_FLOW_CYCLE',
        target: found.key,
        message: `Se corto un ciclo de flujo al expandir ${found.key}.`,
      })
      return
    }
    walkActions(found.value || [], state, [...stack, found.key])
  }

  return function reachableFromActions(actions = []) {
    const state = { forms: [], menus: [], actionCount: 0 }
    walkActions(actions, state, [])
    return state
  }
}

function rawFormIndex(dataJson = {}, logicJson = {}) {
  const out = {}
  ;(dataJson.forms || [])
    .filter((form) => String(form.windowType || '').toLowerCase() !== 'menu')
    .forEach((form) => {
      ;[form.id, form.legacyId, form.title].filter(Boolean).forEach((key) => {
        const normalized = normalizeName(key)
        if (normalized && !out[normalized]) out[normalized] = form
      })
    })
  Object.entries(logicJson.forms || {}).forEach(([id, form]) => {
    const normalized = normalizeName(id)
    if (!out[normalized]) out[normalized] = { ...form, fields: form.fields || [] }
  })
  return out
}

function cloneRouteForm(sourceForm, sourceId, route, group, usedFormIds) {
  const formId = uniqueId(`sst_${route}_${sourceId}`, usedFormIds)
  const fields = (sourceForm.fields || []).flatMap((field, index) => mapField(field, {
    formId,
    sourceFormId: sourceId,
    index,
    group: `*${group}*`,
    route,
  }))
  return compactObject({
    form: formId,
    id: formId,
    title: sourceForm.title || sourceId || 'Formulario',
    title_visible: true,
    windowType: sourceForm.windowType || 'form',
    remarks: fields,
    source_additional: {
      source: 'sst_converter',
      sourceForm: sourceId,
      sourceLine: sourceForm.sourceLine || sourceForm.line,
      route,
      routeGroup: group,
      layout: compactObject({ row: sourceForm.row, column: sourceForm.column, raw: sourceForm.raw }),
    },
  })
}

function createMenuForm(menu, context) {
  const formId = uniqueId(`sst_menu_${context.path.join('_')}`, context.usedFormIds)
  const fieldId = uniqueId(`${formId}_select`, context.usedFieldIds)
  const datasource = []
  const optionPlans = []

  ;(menu.options || []).forEach((option, index) => {
    const label = String(option.text || option.label || option.value || `Opcion ${index + 1}`).trim()
    const optionRoute = uniqueId(`${context.path.join('_')}_${label}`, context.usedRouteIds)
    datasource.push({
      text: label,
      value: optionRoute,
      sourceValue: option.value,
      terminal: option.terminal === true,
    })
    context.metrics.menuOptions += 1

    const actionList = option.actionRef ? context.logicJson.menuActions?.[option.actionRef] || [] : []
    const reachable = context.reachableFromActions(actionList)
    const childMenus = [option.targetMenu, ...reachable.menus].filter(Boolean)
    const uniqueChildMenus = [...new Set(childMenus.map((target) => findByNormalizedName(context.logicJson.menus || {}, target)?.key).filter(Boolean))]
    optionPlans.push({ option, label, optionRoute, reachable, childMenus: uniqueChildMenus })
  })

  context.forms.push({
    form: formId,
    id: formId,
    title: menu.title || context.menuId,
    title_visible: true,
    windowType: 'menu',
    remarks: [{
      tag: fieldId,
      campoForm: fieldId,
      label: menu.title || 'Selecciona una opcion',
      typedata: 'select',
      visible: true,
      mandatory: true,
      datasource,
      dato: '',
      remark: '',
      remark_mask: '',
      skip: true,
      processed: false,
      group: context.parentGroup ? `*${context.parentGroup}*` : undefined,
      source_additional: {
        source: 'sst_converter',
        kind: 'menu_select',
        menuId: context.menuId,
        route: context.path.join(' > '),
        targets: datasource.map((option) => option.value),
        autoAdvance: false,
      },
    }],
    source_additional: {
      source: 'sst_converter',
      kind: 'menu',
      menuId: context.menuId,
      sourceLine: menu.sourceLine,
      depth: context.depth,
      parentGroup: context.parentGroup || '',
    },
  })
  context.metrics.menuForms += 1
  context.metrics.maxMenuDepth = Math.max(context.metrics.maxMenuDepth, context.depth)

  optionPlans.forEach(({ option, label, optionRoute, reachable, childMenus }) => {
    const routeFormIds = []
    reachable.forms.forEach((sourceFormId) => {
      const sourceForm = context.formIndex[normalizeName(sourceFormId)]
      if (!sourceForm) {
        context.diagnostics.push({
          level: 'warning',
          code: 'BOSTON_ADAPTER_FORM_NOT_FOUND',
          formId: sourceFormId,
          route: optionRoute,
          message: `No se encontro el formulario ${sourceFormId} de la ruta ${label}.`,
        })
        return
      }
      const mapped = cloneRouteForm(sourceForm, sourceFormId, optionRoute, optionRoute, context.usedFormIds)
      routeFormIds.push(mapped.form)
      context.forms.push(mapped)
      context.reachedSourceForms.add(normalizeName(sourceFormId))
      context.metrics.routeForms += 1
    })

    let builtChildren = 0
    childMenus.forEach((childMenuId) => {
      const built = buildMenuRoute(childMenuId, {
        ...context,
        parentGroup: optionRoute,
        path: [...context.path, optionRoute],
        depth: context.depth + 1,
        ancestry: [...context.ancestry, context.menuId],
      })
      if (built) builtChildren += 1
    })

    if (!builtChildren) {
      context.actions.push({
        id: optionRoute,
        label,
        visible: true,
        enabled: true,
        commands: [],
        source_additional: compactObject({
          kind: 'sst_route_projection',
          action: 'interpreter_managed',
          executionReady: true,
          sourceMenu: context.menuId,
          sourceValue: option.value,
          actionRef: option.actionRef,
          terminal: option.terminal === true,
          targetForms: routeFormIds,
        }),
      })
    }
  })
}

function buildMenuRoute(menuId, context) {
  const found = findByNormalizedName(context.logicJson.menus || {}, menuId)
  if (!found) {
    context.diagnostics.push({
      level: 'warning',
      code: 'BOSTON_ADAPTER_MENU_NOT_FOUND',
      menuId,
      message: `No se encontro el menu ${menuId}.`,
    })
    return false
  }
  if (context.depth > 20 || context.ancestry.includes(found.key)) {
    context.diagnostics.push({
      level: 'warning',
      code: 'BOSTON_ADAPTER_MENU_CYCLE',
      menuId: found.key,
      message: `Se corto un ciclo de menus en ${found.key}.`,
    })
    return false
  }
  createMenuForm(found.value, { ...context, menuId: found.key })
  return true
}

function buildMenuAwareForms(dataJson, logicJson, diagnostics) {
  const menus = logicJson.menus || {}
  const allMenuIds = Object.keys(menus)
  const targetedMenus = new Set()
  Object.values(menus).forEach((menu) => {
    ;(menu.options || []).forEach((option) => {
      const found = findByNormalizedName(menus, option.targetMenu)
      if (found) targetedMenus.add(found.key)
    })
  })
  const roots = allMenuIds.filter((menuId) => !targetedMenus.has(menuId))
  const rootMenus = roots.length ? roots : allMenuIds.slice(0, 1)
  const context = {
    logicJson,
    diagnostics,
    formIndex: rawFormIndex(dataJson, logicJson),
    reachableFromActions: createProgramWalker(logicJson, diagnostics),
    forms: [],
    actions: [],
    reachedSourceForms: new Set(),
    usedFormIds: new Set(),
    usedFieldIds: new Set(),
    usedRouteIds: new Set(),
    metrics: { menuForms: 0, routeForms: 0, menuOptions: 0, maxMenuDepth: 0 },
  }

  if (!rootMenus.length) {
    ;(dataJson.forms || [])
      .filter((form) => String(form.windowType || '').toLowerCase() !== 'menu')
      .forEach((sourceForm, index) => {
        const sourceId = sourceForm.id || sourceForm.legacyId || `FORM_${index + 1}`
        const formId = uniqueId(`sst_${sourceId}`, context.usedFormIds)
        const remarks = (sourceForm.fields || []).flatMap((field, fieldIndex) => mapField(field, {
          formId,
          sourceFormId: sourceId,
          index: fieldIndex,
          group: undefined,
          route: 'linear',
        }))
        context.forms.push({
          form: formId,
          id: formId,
          title: sourceForm.title || sourceId,
          title_visible: true,
          windowType: sourceForm.windowType || 'form',
          remarks,
          source_additional: { source: 'sst_converter', sourceForm: sourceId, route: 'linear' },
        })
        context.reachedSourceForms.add(normalizeName(sourceId))
      })
  } else {
    rootMenus.forEach((menuId, index) => buildMenuRoute(menuId, {
      ...context,
      parentGroup: '',
      path: [`root_${index + 1}`, menuId],
      depth: 1,
      ancestry: [],
    }))
  }

  const sourceFormIds = (dataJson.forms || [])
    .filter((form) => String(form.windowType || '').toLowerCase() !== 'menu')
    .map((form) => normalizeName(form.id || form.legacyId || form.title))
    .filter(Boolean)
  const unmappedForms = [...new Set(sourceFormIds.filter((id) => !context.reachedSourceForms.has(id)))]
  if (rootMenus.length && unmappedForms.length) {
    diagnostics.push({
      level: 'info',
      code: 'BOSTON_ADAPTER_UNMAPPED_FORMS',
      count: unmappedForms.length,
      forms: unmappedForms,
      message: `${unmappedForms.length} formulario(s) no quedaron asociados a una opcion de menu; se conservan en logic.json, no en la vista activa.`,
    })
  }

  return {
    forms: context.forms,
    actions: context.actions,
    stats: {
      sourceForms: sourceFormIds.length,
      outputForms: context.forms.length,
      menuForms: context.metrics.menuForms,
      routeForms: context.metrics.routeForms,
      menuOptions: context.metrics.menuOptions,
      maxMenuDepth: context.metrics.maxMenuDepth,
      unmappedForms: unmappedForms.length,
    },
  }
}

/**
 * Adapta los artefactos canónicos del convertidor al contrato visual vigente de Boston.
 * Conserva la proyección visual Boston para edición y adjunta el data.json canónico
 * que consume el intérprete SST. El modo queda aislado por executionMode para no
 * cambiar el runtime de scripts creados por descripción.
 */
export function adaptSstConversionToBoston(conversion = {}, options = {}) {
  const sourceDataJson = conversion.dataJson || {}
  const sourceLogicJson = conversion.logicJson || {}
  const diagnostics = []
  const mapped = buildMenuAwareForms(sourceDataJson, sourceLogicJson, diagnostics)
  const title = sourceDataJson.title || conversion.ir?.title || options.title || conversion.source?.fileName || 'Script SST'

  const dataJson = {
    title,
    scriptType: 'custom',
    executionMode: 'sst_interpreter_v1',
    editCatalogues: false,
    reintentos: 0,
    sendCommands: true,
    orderExecute: true,
    validateActiveReservation: false,
    showVerification: true,
    forms: mapped.forms,
    actions: mapped.actions,
    postCommands: [],
    sstRuntime: {
      schemaVersion: 1,
      data: sourceDataJson,
    },
    source_additional: {
      source: 'sst_converter',
      adapter: BOSTON_ADAPTER_VERSION,
      converter: conversion.version?.integration || 'boston-sst-converter-v1',
      schemaVersion: 1,
      previewRenderer: 'sst_native_interpreter',
      fileName: conversion.source?.fileName || options.fileName || '',
      entryBlock: sourceLogicJson.entryBlock || '',
      executionReady: true,
      previewOnly: false,
      logicFile: 'logic.json',
    },
  }

  const logicJson = {
    ...sourceLogicJson,
    adapter: {
      id: BOSTON_ADAPTER_VERSION,
      schemaVersion: 1,
      executionReady: true,
      previewOnly: false,
    },
  }

  return {
    adapterVersion: BOSTON_ADAPTER_VERSION,
    dataJson,
    logicJson,
    diagnostics,
    stats: mapped.stats,
  }
}

export { BOSTON_ADAPTER_VERSION, BOSTON_FIELD_TYPES }
